import { makeStyles, tokens } from '@fluentui/react-components';
import { CSSProperties } from 'react';
import { customPalette } from '../theme';

export interface IEditableGridStyles {
    gridContainerStyles: string;
    toolbarStyles: string;
    footerStyles: string;
    gridComponentCSSClass: string;
    totalRowStyle: CSSProperties;
}

const useEditableGridClassNames = makeStyles({
    gridContainerStyles: {
        width: '100%',
        height: '100%',
        '& .e-grid td.e-updatedtd': {
            backgroundColor: 'inherit',
            color: 'inherit',
        },
    },
    toolbarStyles: {
        '& .e-grid': {
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
        },
        '& .e-grid .e-toolbar, & .e-grid .e-toolbar .e-toolbar-items, & .e-grid .e-toolbar .e-toolbar-items .e-toolbar-item.e-overlay': {
            backgroundColor: tokens.colorNeutralBackground1,
            minHeight: '40px',
        },
        '& .e-grid .e-toolbar .e-toolbar-item .e-btn': {
            height: '32px',
            minHeight: 'unset',
            borderRadius: '4px',
            padding: '5px 12px',
        },
        '& .e-grid .e-toolbar .e-toolbar-item:first-child .e-btn': {
            backgroundColor: tokens.colorBrandBackground,
            '& .e-tbar-btn-text, & .e-btn-icon': {
                color: tokens.colorNeutralBackground1,
            },
        },
    },
    footerStyles: {
        '& .e-gridfooter .e-summarycontent .e-table .e-summaryrow .e-summarycell': {
            borderTop: `2px solid ${tokens.colorNeutralForeground3}`,
            backgroundColor: tokens.colorNeutralBackground1,
            paddingRight: '10px',
        },
        '& .e-grid .e-footerpadding': {
            paddingRight: '0px',
        },
    },
    gridComponent: {
        border: '0 !important',
        overflow: 'inherit',
        '&.e-grid': {
            display: 'flex',
            flexDirection: 'column',
            height: 'inherit',
            '& .e-rowcell, & .e-headercell': {
                padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalL}`,
                '&:first-child, &:last-child': {
                    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalL}`,
                    lineHeight: '20px',
                },
            },
            '& .e-editedbatchcell.e-rowcell, & .e-editedbatchcell.e-headercell': {
                padding: `${tokens.spacingVerticalNone} ${tokens.spacingHorizontalNone}`,
                '&:first-child, &:last-child': {
                    padding: `${tokens.spacingVerticalNone} ${tokens.spacingHorizontalNone}`,
                },
            },
            '& .e-row .e-input-group': {
                margin: 0,
                '& input.e-input': {
                    height: 'auto',
                    padding: `${tokens.spacingVerticalNone} ${tokens.spacingHorizontalS}`,
                },
            },
            '&.e-grid .e-rowcell': {
                border: 'none',
                borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                '&:last-child': {
                    borderRight: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                },
            },
            '& .e-gridheader .e-headercontent .e-columnheader .e-headercell.e-lastcell': {
                '& .e-headercelldiv': {
                    marginRight: '-5px',
                },
                '& .e-icon-filter': {
                    display: 'none',
                },
            },
            '& td.e-updatedtd': {
                backgroundColor: 'inherit',
                color: 'inherit',
            },
            '& .e-summarycell': {
                border: 'none',
            },
            '& .e-gridfooter': {
                backgroundColor: '#FFFFFF',
            },
        },
        '& .e-gridheader': {
            border: '0 !important',
            flex: '28px 0 0',
            overflow: 'hidden auto !important',
            scrollbarGutter: 'stable',
            paddingRight: '0 !important',
            '& .e-headercontent .e-table .e-columnheader': {
                borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                alignItems: 'stretch',
                '& .e-headercell': {
                    backgroundColor: customPalette.blueC7DCE5,
                    '& .e-headercelldiv .e-headertext': {
                        fontWeight: tokens.fontSizeBase500,
                    },
                },
            },
        },
        // Override CorpTax left/right borders on header cells only.
        // Do not include tr td:first-child — that wiped Account's right border
        // and stacked border-top + border-bottom on Account body cells.
        '&.e-grid:not(.sf-grid).e-default .e-headercell, &.e-grid.e-default:not(.e-rtl) tr th.e-headercell:first-child:not(.e-firstcell):not(.e-headercell.e-frozen-left-border), &.e-grid.e-default:not(.e-rtl) tr th.e-filterbarcell:first-child:not(.e-filterbarcell.e-frozen-left-border)':
            {
                border: 'none',
                borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                '&:last-child': {
                    borderRight: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                },
                '&:first-child': {
                    borderLeft: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                },
            },
        '& .e-gridcontent': {
            overflow: 'hidden auto !important',
            scrollbarGutter: 'stable',
            flex: '0px 1 1',
            '& .e-content .e-table': {
                '& .e-row .e-rowcell, & .e-summaryrow .e-rowcell': {
                    color: tokens.colorNeutralForeground1,
                    fontWeight: 400,
                    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                },
                '& .disabledRow > td': {
                    backgroundColor: `${customPalette.statusCoolMidGrey} !important`,
                },
            },
        },
        '& .e-gridfooter': {
            border: '0 !important',
            overflow: 'hidden auto !important',
            scrollbarGutter: 'stable',
            flex: '36px 0 0',
            paddingRight: '0 !important',
            '& .e-summarycontent': {
                overflow: 'hidden !important',
                borderRadius: 'inherit',
                borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
                '& .e-table .e-summaryrow .e-summarycell': {
                    backgroundColor: '#FFFFFF',
                    color: tokens.colorNeutralForeground1,
                    fontWeight: tokens.fontWeightSemibold,
                    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalL}`,
                    textOverflow: 'ellipsis',
                    textWrap: 'nowrap',
                    overflow: 'hidden',
                },
            },
        },
    },
});

export const useEditableGridStyles: () => IEditableGridStyles = () => {
    const { gridContainerStyles, toolbarStyles, footerStyles, gridComponent } = useEditableGridClassNames();
    const totalRowStyle: CSSProperties = {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '16px',
        verticalAlign: 'top',
        lineHeight: '22px',
    };

    return {
        gridContainerStyles,
        toolbarStyles,
        footerStyles,
        gridComponentCSSClass: gridComponent,
        totalRowStyle,
    };
};
